import React, { createContext, useMemo, useEffect, ReactNode } from 'react';
import { useCurrentSession } from '../hooks/useCurrentSession';
import { Session } from 'src/api/types/session';
import { traceLog } from '../utils/requestTrace';

export interface CurrentSessionPlayerContextType {
  isVisible: boolean;
  isLoading: boolean;
  error: string | null;
  currentSession: Session | null;
  refetch: (source?: string) => Promise<void>;
  stopSession: () => Promise<void>;
  showAssociationModal: boolean;
  setShowAssociationModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAssociationComplete: () => void;
}

const CurrentSessionPlayerContext = createContext<CurrentSessionPlayerContextType | undefined>(
  undefined
);

interface CurrentSessionPlayerProviderProps {
  children: ReactNode;
}

export const CurrentSessionPlayerProvider: React.FC<CurrentSessionPlayerProviderProps> = ({
  children
}) => {
  const {
    currentSession,
    loading,
    error,
    refetch,
    stopSession,
    showAssociationModal,
    setShowAssociationModal,
    handleAssociationComplete
  } = useCurrentSession();
  const isVisible = !loading && !error && !!currentSession;

  useEffect(() => {
    traceLog('current-session-provider', 'mounted');
    return () => {
      traceLog('current-session-provider', 'unmounted');
    };
  }, []);

  const contextValue: CurrentSessionPlayerContextType = useMemo(
    () => ({
      isVisible,
      isLoading: loading,
      error,
      currentSession,
      refetch,
      stopSession,
      showAssociationModal,
      setShowAssociationModal,
      handleAssociationComplete
    }),
    [
      isVisible,
      loading,
      error,
      currentSession,
      refetch,
      stopSession,
      showAssociationModal,
      setShowAssociationModal,
      handleAssociationComplete
    ]
  );

  return <CurrentSessionPlayerContext value={contextValue}>{children}</CurrentSessionPlayerContext>;
};

export { CurrentSessionPlayerContext };
